import React, {useReducer} from 'react';
import {Application} from '../../backend/interfaces/application';
import {ApplicationCategory} from '../../backend/interfaces/application-category';
import {Entity} from '../../backend/interfaces/entity';
import {User} from '../../backend/interfaces/user';
import {SubmitButtonState} from '../../components/submit-button/submit-button';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {InviteIdentification} from '../../interfaces/invite-identification';
import {getAreWeRunningInMobile} from '../../utils/functions/get-are-we-running-in-mobile';
import {OfficeContext} from './context';
import {
  IncomingCallState,
  Occurrence,
  OfficeContextActionType,
  OfficeContextState,
  VideocallState,
  VoximplantProcessState,
  VoximplantState,
} from './types';
import {UserCard} from '../../backend/interfaces/user-card';
import {Device} from '../../backend/interfaces/device';
import {Playlist} from '../../backend/interfaces/playlist';
import {PlaylistItem} from '../../backend/interfaces/playlist-item';
import reducer from './reducer';
import {Affiliation} from '../../backend/interfaces/affiliation';
import {AffiliationQuickAccess} from '../../backend/interfaces/affiliation-quick-access';

// Set Initial State
const initialState: OfficeContextState = {
  pendingTasksVisible: false,
  drawerOpened: getAreWeRunningInMobile() ? false : true,
  drawerCollapsed: false, // true to be collapsed on start
  drawerCollapeDisabled: true, // false to be collapsed on start
  sideNavigationDrawerCollapsed: false,
  currentCaregiverModeEnabled: CaregiverMode._None,
  userForm: undefined,
  userFormLoadingState: true,
  userFormDeleteButtonState: undefined,
  userFormSubmitButtonState: undefined,
  entityForm: undefined,
  entityFormLoadingState: true,
  entityFormDeleteButtonState: undefined,
  entityFormSubmitButtonState: undefined,
  userCardForm: undefined,
  userCardFormSubmitButtonState: undefined,
  userCardFormDeleteButtonState: undefined,
  deviceForm: undefined,
  deviceFormDeleteButtonState: undefined,
  deviceFormSubmitButtonState: undefined,
  applicationCategoryForm: undefined,
  applicationCategoryFormSubmitButtonState: undefined,
  applicationCategoryFormDeleteButtonState: undefined,
  playlistForm: undefined,
  playlistFormDeleteButtonState: undefined,
  playlistFormSubmitButtonState: undefined,
  playlistItemForm: undefined,
  playlistItemFormDeleteButtonState: undefined,
  playlistItemFormSubmitButtonState: undefined,
  applicationForm: undefined,
  professionalCaregiverInviteFormSubmitButtonState: undefined,
  informalCaregiverInviteFormSubmitButtonState: undefined,
  associateUserCardFormSubmitButtonState: undefined,
  associateDeviceFormSubmitButtonState: undefined,
  invite: undefined,
  voximplant: {
    sdkVersion: '',
    initState: VoximplantProcessState.Undefined,
    connectionState: VoximplantProcessState.Undefined,
    signedInState: VoximplantProcessState.Undefined,
    signedOutState: VoximplantProcessState.Undefined,
    degraded: false,
  },
  videocall: {
    callIncoming: false,
    callIncomingPendingRecovery: false,
    open: false,
    screenMode: 'full-screen',
    callTimer: undefined,
  },
  occurrence: undefined,
};

export const OfficeContextProvider = (props: {children: React.ReactNode}) => {
  // Create Reducer
  const [state, dispatch] = useReducer(reducer, initialState);

  // Hide pending tasks
  const showPendingTasks = (pendingTasksVisible: boolean) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.HIDE_PENDING_TASKS,
      pendingTasksVisible: pendingTasksVisible,
    });
  };

  // Toggle drawer
  const toggleDrawer = (drawerOpened: boolean): void => {
    // Update Session
    dispatch({
      type: OfficeContextActionType.TOGGLE_DRAWER,
      drawerOpened,
    });
  };

  // Toggle drawer
  const toggleSideNavigationMenuDrawer = (sideNavigationDrawerCollapsed: boolean): void => {
    // Dispatch update
    dispatch({
      type: OfficeContextActionType.TOGGLE_NAVIGATION_DRAWER,
      sideNavigationDrawerCollapsed,
    });
  };

  // Set current caregiver mode enabled
  const setCurrentCaregiverModeEnabled = (mode: CaregiverMode) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_CURRENT_CAREGIVER_MODE_ENABLED,
      mode,
    });
  };

  // Set the user in the UserForm component
  const setUserForm = (
    user: User,
    affiliation?: Affiliation,
    affiliationQuickAccess?: AffiliationQuickAccess
  ) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_FORM,
      user,
      affiliation,
      affiliationQuickAccess,
    });
  };

  // Set the load state for the UserForm component
  const setUserFormLoadState = (value: boolean) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_FORM_LOAD_STATE,
      state: value,
    });
  };

  // Set the submit button state in the UserForm component
  const setUserFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the UserForm component
  const setUserFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Set the submit button state in the invite caregiver screen
  const setInformalCaregiverInviteFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the user in the UserForm component
  const clearUserForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_USER_FORM,
    });
  };

  // Clear the submit button state in the UserForm component
  const clearUserFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_USER_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the UserForm component
  const clearUserFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_USER_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Clear the submit button state in the invite caregiver screen
  const clearInformalCaregiverInviteFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Set the application in the ApplicationForm component
  const setApplicationForm = (application: Application) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_APPLICATION_FORM,
      application,
    });
  };

  // Clear the application in the ApplicationForm component
  const clearApplicationForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_APPLICATION_FORM,
    });
  };

  // Clear invite
  const clearInvite = () => {
    // Update
    dispatch({
      type: OfficeContextActionType.CLEAR_INVITE,
    });
  };

  // Set invite
  const setInvite = (invite: InviteIdentification) => {
    // Update
    dispatch({
      type: OfficeContextActionType.SET_INVITE,
      invite,
    });
  };

  const clearOccurrence = () => {
    // Update
    dispatch({
      type: OfficeContextActionType.CLEAR_OCCURRENCE,
    });
  };

  const setOccurrence = (occurrence: Occurrence) => {
    // Update
    dispatch({
      type: OfficeContextActionType.SET_OCCURRENCE,
      occurrence: {
        id: occurrence.id,
        entityId: occurrence.entityId,
        date: occurrence.date,
        appAlreadyOpened: occurrence.appAlreadyOpened,
      },
    });
  };

  // Set the entity in the EntityForm component
  const setEntityForm = (entity: Entity) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_ENTITY_FORM,
      entity,
    });
  };

  // Set the load state for the entity form component
  const setEntityFormLoadState = (value: boolean) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_ENTITY_FORM_LOAD_STATE,
      state: value,
    });
  };

  // Clear the entity in the EntityForm component
  const clearEntityForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_ENTITY_FORM,
    });
  };

  // Set the application category in the ApplicationCategoryForm component
  const setApplicationCategoryForm = (applicationCategory: ApplicationCategory) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_APPLICATION_CATEGORY_FORM,
      applicationCategory,
    });
  };

  // Clear the application category in the ApplicationCategoryForm component
  const clearApplicationCategoryForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_APPLICATION_CATEGORY_FORM,
    });
  };

  // Set the submit button state in the ApplicationCategoryForm component
  const setApplicationCategoryFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the ApplicationCategoryForm component
  const setApplicationCategoryFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the ApplicationCategoryForm component
  const clearApplicationCategoryFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the ApplicationCategoryForm component
  const clearApplicationCategoryFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Set the submit button state in the EntityForm component
  const setEntityFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_ENTITY_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the EntityForm component
  const setEntityFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_ENTITY_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the EntityForm component
  const clearEntityFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_ENTITY_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the EntityForm component
  const clearEntityFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_ENTITY_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Set the submit button state in the ProfessionalCaregiverInviteFrom component
  const setProfessionalCaregiverInviteFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the ProfessionalCaregiverInviteFrom component
  const clearProfessionalCaregiverInviteFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Set the submit button state in the AssociateUserCardFormForm component
  const setAssociateUserCardFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the AssociateUserCardFormForm component
  const clearAssociateUserCardFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Set the submit button state in the associate device screen
  const setAssociateDeviceFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the associate device screen
  const clearAssociateDeviceFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Set the user card in the UserCardForm component
  const setUserCardForm = (userCard: UserCard) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_CARD_FORM,
      userCard,
    });
  };

  // Clear the user card in the UserCardForm component
  const clearUserCardForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_USER_CARD_FORM,
    });
  };

  // Set the submit button state in the UserCardForm component
  const setUserCardFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_CARD_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the UserCardForm component
  const setUserCardFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_USER_CARD_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the UserCardForm component
  const clearUserCardFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_USER_CARD_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the UserCardForm component
  const clearUserCardFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_USER_CARD_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Set the device in the DeviceForm component
  const setDeviceForm = (device: Device) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_DEVICE_FORM,
      device,
    });
  };

  // Clear the device in the DeviceForm component
  const clearDeviceForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_DEVICE_FORM,
    });
  };

  // Set the submit button state in the DeviceForm component
  const setDeviceFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_DEVICE_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the DeviceForm component
  const setDeviceFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_DEVICE_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the DeviceForm component
  const clearDeviceFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_DEVICE_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the DeviceForm component
  const clearDeviceFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_DEVICE_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Set the playlist in the PlaylistForm component
  const setPlaylistForm = (playlist: Playlist) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PLAYLIST_FORM,
      playlist,
    });
  };

  // Clear the playlist in the PlaylistForm component
  const clearPlaylistForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PLAYLIST_FORM,
    });
  };

  // Set the submit button state in the PlaylistForm component
  const setPlaylistFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PLAYLIST_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the PlaylistForm component
  const setPlaylistFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PLAYLIST_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the PlaylistForm component
  const clearPlaylistFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PLAYLIST_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the PlaylistForm component
  const clearPlaylistFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PLAYLIST_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Set the playlist item in the PlaylistItemForm component
  const setPlaylistItemForm = (playlistItem: PlaylistItem) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PLAYLIST_ITEM_FORM,
      playlistItem,
    });
  };

  // Clear the playlist item in the PlaylistItemForm component
  const clearPlaylistItemForm = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PLAYLIST_ITEM_FORM,
    });
  };

  // Set the submit button state in the PlaylistItemForm component
  const setPlaylistItemFormSubmitButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE,
      state: value,
    });
  };

  // Set the delete button state in the PlaylistItemForm component
  const setPlaylistItemFormDeleteButtonState = (value: SubmitButtonState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE,
      state: value,
    });
  };

  // Clear the submit button state in the PlaylistItemForm component
  const clearPlaylistItemFormSubmitButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE,
    });
  };

  // Clear the delete button state in the PlaylistItemForm component
  const clearPlaylistItemFormDeleteButtonState = () => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.CLEAR_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE,
    });
  };

  // Update Voximplant State
  const updateVoximplantState = (state: VoximplantState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.UPDATE_VOXIMPLANT_STATE,
      state,
    });
  };

  // Update Videocall state
  const updateVideocallState = (state: VideocallState) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.UPDATE_VIDEOCALL_STATE,
      state,
    });
  };

  // Set incoming call state
  const setIncomingCall = (state: IncomingCallState | undefined) => {
    // Dispatch
    dispatch({
      type: OfficeContextActionType.SET_INCOMING_CALL,
      state,
    });
  };

  // Collapse drawer
  const collapseDrawer = (drawerCollapsed: boolean, drawerCollapeDisabled: boolean): void => {
    // Update Session
    dispatch({
      type: OfficeContextActionType.COLLAPSE_DRAWER,
      drawerCollapsed,
      drawerCollapeDisabled,
    });
  };

  return (
    <OfficeContext.Provider
      value={{
        state,
        // Methods
        showPendingTasks,
        toggleDrawer,
        toggleSideNavigationMenuDrawer,
        setCurrentCaregiverModeEnabled,
        setUserForm,
        setUserFormLoadState,
        setUserFormSubmitButtonState,
        setUserFormDeleteButtonState,
        setInformalCaregiverInviteFormSubmitButtonState,
        clearUserForm,
        clearUserFormSubmitButtonState,
        clearUserFormDeleteButtonState,
        clearInformalCaregiverInviteFormSubmitButtonState,
        setApplicationForm,
        clearApplicationForm,
        clearInvite,
        setInvite,
        setOccurrence,
        clearOccurrence,
        setEntityForm,
        setEntityFormLoadState,
        clearEntityForm,
        setApplicationCategoryForm,
        clearApplicationCategoryForm,
        setApplicationCategoryFormSubmitButtonState,
        clearApplicationCategoryFormSubmitButtonState,
        setApplicationCategoryFormDeleteButtonState,
        clearApplicationCategoryFormDeleteButtonState,
        setEntityFormSubmitButtonState,
        clearEntityFormSubmitButtonState,
        setEntityFormDeleteButtonState,
        clearEntityFormDeleteButtonState,
        setProfessionalCaregiverInviteFormSubmitButtonState,
        clearProfessionalCaregiverInviteFormSubmitButtonState,
        setAssociateUserCardFormSubmitButtonState,
        clearAssociateUserCardFormSubmitButtonState,
        setAssociateDeviceFormSubmitButtonState,
        clearAssociateDeviceFormSubmitButtonState,
        setUserCardForm,
        setUserCardFormDeleteButtonState,
        setUserCardFormSubmitButtonState,
        clearUserCardForm,
        clearUserCardFormDeleteButtonState,
        clearUserCardFormSubmitButtonState,
        setDeviceForm,
        clearDeviceForm,
        setDeviceFormDeleteButtonState,
        clearDeviceFormDeleteButtonState,
        setDeviceFormSubmitButtonState,
        clearDeviceFormSubmitButtonState,
        setPlaylistForm,
        clearPlaylistForm,
        setPlaylistFormDeleteButtonState,
        clearPlaylistFormDeleteButtonState,
        setPlaylistFormSubmitButtonState,
        clearPlaylistFormSubmitButtonState,
        setPlaylistItemForm,
        clearPlaylistItemForm,
        setPlaylistItemFormDeleteButtonState,
        clearPlaylistItemFormDeleteButtonState,
        setPlaylistItemFormSubmitButtonState,
        clearPlaylistItemFormSubmitButtonState,
        updateVoximplantState,
        updateVideocallState,
        setIncomingCall,
        collapseDrawer,
      }}
    >
      {props.children}
    </OfficeContext.Provider>
  );
};
